import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Spin,
  Switch,
  Tag,
  Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { baseURL } from '../../globalUtils/axiosConfig';
import './index.less';

const url = `${baseURL}/api/v2/super-admin/apns`;

const APP_LABELS = {
  parent_app: 'Community (parent) app',
  teacher_app: 'Teacher app',
};

const fmtDate = (d) => (d ? moment(d).format('D MMM YYYY, h:mm:ss a') : '—');

const PushEvent = ({ event, kind }) => {
  if (!event) return <span className='muted'>None recorded yet</span>;
  return (
    <div className='push-event'>
      <div>
        <strong>{fmtDate(event.at)}</strong>{' '}
        <Tag>{APP_LABELS[event.app] || event.app}</Tag>
        <Tag>{event.environment}</Tag>
        {event.tenant && <Tag>tenant: {event.tenant}</Tag>}
      </div>
      <div className='muted'>
        topic {event.topic} · device …{event.device || '?'}
        {kind === 'failure' && (
          <>
            {' '}
            · status {event.status || '-'} · <strong>{event.reason}</strong>
          </>
        )}
      </div>
    </div>
  );
};

const PushSettings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [keyFile, setKeyFile] = useState(null);
  const [checkEnv, setCheckEnv] = useState('production');
  const [checkApp, setCheckApp] = useState('parent_app');
  const [checkResult, setCheckResult] = useState(null);
  const [form] = Form.useForm();

  const applyStatus = useCallback(
    (apns) => {
      setStatus(apns);
      form.setFieldsValue({
        key_id: apns.key_id || '',
        team_id: apns.team_id || '',
        parent_topic: apns.topics?.parent_app || '',
        teacher_topic: apns.topics?.teacher_app || '',
        enabled: apns.enabled !== false,
      });
    },
    [form]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(url);
      applyStatus(response.data?.data?.apns);
    } catch (error) {
      message.error(
        error?.response?.data?.message || 'Could not load APNs settings'
      );
    } finally {
      setLoading(false);
    }
  }, [applyStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (values) => {
    setSaving(true);
    try {
      const fd = new FormData();
      ['key_id', 'team_id', 'parent_topic', 'teacher_topic'].forEach((k) => {
        if (values[k] !== undefined && values[k] !== null) {
          fd.append(k, String(values[k]).trim());
        }
      });
      fd.append('enabled', values.enabled ? 'true' : 'false');
      if (keyFile) fd.append('key', keyFile, keyFile.name);
      const response = await axios.put(url, fd, {
        headers: { 'content-type': 'multipart/form-data' },
      });
      message.success(response.data?.message || 'Saved');
      setKeyFile(null);
      setCheckResult(null);
      applyStatus(response.data?.data?.apns);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const onCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    try {
      const response = await axios.post(`${url}/validate`, {
        environment: checkEnv,
        app: checkApp,
      });
      setCheckResult(response.data?.data?.result);
      applyStatus(response.data?.data?.apns);
    } catch (error) {
      setCheckResult({
        ok: false,
        message: error?.response?.data?.message || 'Check failed',
      });
    } finally {
      setChecking(false);
    }
  };

  const onRemoveKey = async () => {
    setRemoving(true);
    try {
      const response = await axios.delete(`${url}/key`);
      message.success(response.data?.message || 'Key removed');
      applyStatus(response.data?.data?.apns);
    } catch (error) {
      message.error(error?.response?.data?.message || 'Could not remove key');
    } finally {
      setRemoving(false);
    }
  };

  const configured = !!status?.configured;

  return (
    <div className='page-top-div push-settings-page'>
      <Row justify='space-between' align='middle'>
        <Col>
          <span className='text-span'>Push Notifications (Apple VoIP)</span>
        </Col>
        <Col>
          <Button size='small' onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Row gutter={[20, 20]} className='push-settings-rows'>
          <Col span={24}>
            <Card
              size='small'
              title={
                <span>
                  Status{' '}
                  {status &&
                    (configured ? (
                      status.enabled ? (
                        <Tag color='green'>Configured</Tag>
                      ) : (
                        <Tag color='orange'>Configured, switched off</Tag>
                      )
                    ) : (
                      <Tag color='red'>Not configured</Tag>
                    ))}
                </span>
              }
            >
              {status && !status.encryption_configured && (
                <Alert
                  type='error'
                  showIcon
                  className='mb-10'
                  message='APNS_KEY_ENCRYPTION_KEY is not set on the API server, so a .p8 key cannot be stored.'
                />
              )}
              {status && !configured && (
                <Alert
                  type='warning'
                  showIcon
                  className='mb-10'
                  message='VoIP call pushes to iOS are currently disabled.'
                  description={status.key_error}
                />
              )}
              {status?.provider_warning && configured && (
                <Alert
                  type='warning'
                  showIcon
                  className='mb-10'
                  message={status.provider_warning}
                />
              )}
              <Descriptions size='small' column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label='Credential source'>
                  {status?.source === 'database'
                    ? 'Uploaded key (stored encrypted)'
                    : status?.source === 'environment'
                    ? 'Key file on the API server (.env)'
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label='Team ID'>
                  {status?.team_id || '—'}
                </Descriptions.Item>
                <Descriptions.Item label='Key ID'>
                  {status?.key_id || '—'}
                </Descriptions.Item>
                <Descriptions.Item label='Key fingerprint'>
                  {status?.key_fingerprint ? (
                    <code>{status.key_fingerprint}</code>
                  ) : (
                    '—'
                  )}
                </Descriptions.Item>
                <Descriptions.Item label='Key uploaded'>
                  {fmtDate(status?.key_uploaded_at)}
                  {status?.updated_by ? ` by ${status.updated_by}` : ''}
                </Descriptions.Item>
                <Descriptions.Item label='Topics'>
                  <div>
                    {APP_LABELS.parent_app}: <code>{status?.topics?.parent_app}</code>
                  </div>
                  <div>
                    {APP_LABELS.teacher_app}: <code>{status?.topics?.teacher_app}</code>
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label='Pushes since setup'>
                  {status?.counters?.sent || 0} sent ·{' '}
                  {status?.counters?.failed || 0} failed
                </Descriptions.Item>
                <Descriptions.Item label='Tenant overrides'>
                  {status?.tenant_overrides || 0}
                </Descriptions.Item>
                <Descriptions.Item label='Last successful push' span={2}>
                  <PushEvent event={status?.last_success} kind='success' />
                </Descriptions.Item>
                <Descriptions.Item label='Last failed push' span={2}>
                  <PushEvent event={status?.last_failure} kind='failure' />
                </Descriptions.Item>
                <Descriptions.Item label='Last credential check' span={2}>
                  {status?.last_validation ? (
                    <div className='push-event'>
                      <div>
                        <strong>{fmtDate(status.last_validation.at)}</strong>{' '}
                        {status.last_validation.ok ? (
                          <Tag color='green'>OK</Tag>
                        ) : (
                          <Tag color='red'>Failed</Tag>
                        )}
                        <Tag>{APP_LABELS[status.last_validation.app]}</Tag>
                        <Tag>{status.last_validation.environment}</Tag>
                      </div>
                      <div className='muted'>{status.last_validation.message}</div>
                    </div>
                  ) : (
                    <span className='muted'>Never run</span>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} xl={14}>
            <Card size='small' title='Apple credentials (APNs auth key)'>
              <p className='muted'>
                Create the key in Apple Developer → Certificates, Identifiers &amp;
                Profiles → Keys → “+”, tick <em>Apple Push Notifications service
                (APNs)</em>, then download the <code>.p8</code> file (it can only
                be downloaded once). One key covers both apps and never expires.
                The file is encrypted before it is stored and is never shown
                again.
              </p>
              <Form
                form={form}
                layout='vertical'
                onFinish={onSave}
                requiredMark={false}
                initialValues={{ enabled: true }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name='key_id'
                      label='Key ID'
                      rules={[
                        { required: true, message: 'Required' },
                        {
                          pattern: /^[A-Za-z0-9]{10}$/,
                          message: '10 letters/digits, shown next to the key in Apple Developer',
                        },
                      ]}
                    >
                      <Input placeholder='e.g. 7K3Q2XYZ9A' maxLength={10} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name='team_id'
                      label='Team ID'
                      rules={[
                        { required: true, message: 'Required' },
                        {
                          pattern: /^[A-Za-z0-9]{10}$/,
                          message: '10 letters/digits (top right of Apple Developer)',
                        },
                      ]}
                    >
                      <Input placeholder='e.g. SH66368YN7' maxLength={10} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name='parent_topic'
                      label={`${APP_LABELS.parent_app} VoIP topic`}
                      rules={[
                        { required: true, message: 'Required' },
                        {
                          pattern: /^[A-Za-z0-9.-]+\.voip$/,
                          message: 'Bundle ID followed by .voip',
                        },
                      ]}
                    >
                      <Input placeholder='au.hayatiapp.community.voip' />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name='teacher_topic'
                      label={`${APP_LABELS.teacher_app} VoIP topic`}
                      rules={[
                        { required: true, message: 'Required' },
                        {
                          pattern: /^[A-Za-z0-9.-]+\.voip$/,
                          message: 'Bundle ID followed by .voip',
                        },
                      ]}
                    >
                      <Input placeholder='au.hayatiapp.teacher.voip' />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label='Auth key file (.p8)'>
                      <Upload
                        accept='.p8,.pem'
                        maxCount={1}
                        beforeUpload={(file) => {
                          setKeyFile(file);
                          return false;
                        }}
                        onRemove={() => setKeyFile(null)}
                        fileList={keyFile ? [keyFile] : []}
                      >
                        <Button icon={<UploadOutlined />}>
                          {status?.key_fingerprint
                            ? 'Replace key (rotate)'
                            : 'Choose .p8 file'}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name='enabled'
                      label='VoIP push enabled'
                      valuePropName='checked'
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify='space-between' align='middle'>
                  <Col>
                    <Button
                      type='primary'
                      htmlType='submit'
                      loading={saving}
                      disabled={status && !status.encryption_configured && !!keyFile}
                    >
                      {keyFile ? 'Upload key & save' : 'Save'}
                    </Button>
                  </Col>
                  <Col>
                    {status?.source === 'database' && status?.key_fingerprint && (
                      <Popconfirm
                        title='Remove the stored key? VoIP pushes will stop until a new key is uploaded.'
                        okText='Remove'
                        okButtonProps={{ danger: true }}
                        onConfirm={onRemoveKey}
                      >
                        <Button danger loading={removing}>
                          Remove stored key
                        </Button>
                      </Popconfirm>
                    )}
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>

          <Col xs={24} xl={10}>
            <Card size='small' title='Check credentials with Apple'>
              <p className='muted'>
                Sends a probe to Apple with an invalid device token. No real
                device is contacted; Apple's reply shows whether the key, team
                ID and topic are accepted.
              </p>
              <Row gutter={[8, 8]} align='middle'>
                <Col xs={24} md={10}>
                  <Select
                    value={checkEnv}
                    onChange={setCheckEnv}
                    style={{ width: '100%' }}
                    options={[
                      { value: 'production', label: 'Production (App Store / TestFlight)' },
                      { value: 'sandbox', label: 'Sandbox (Xcode builds)' },
                    ]}
                  />
                </Col>
                <Col xs={24} md={9}>
                  <Select
                    value={checkApp}
                    onChange={setCheckApp}
                    style={{ width: '100%' }}
                    options={Object.keys(APP_LABELS).map((k) => ({
                      value: k,
                      label: APP_LABELS[k],
                    }))}
                  />
                </Col>
                <Col xs={24} md={5}>
                  <Button
                    block
                    onClick={onCheck}
                    loading={checking}
                    disabled={!configured}
                  >
                    Check
                  </Button>
                </Col>
              </Row>
              {checkResult && (
                <Alert
                  className='mt-10'
                  type={checkResult.ok ? 'success' : 'error'}
                  showIcon
                  message={checkResult.ok ? 'Credentials accepted' : 'Check failed'}
                  description={
                    <>
                      {checkResult.message}
                      {checkResult.reason && (
                        <div className='muted'>
                          APNs status {checkResult.status || '-'} ·{' '}
                          {checkResult.reason}
                        </div>
                      )}
                    </>
                  }
                />
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default PushSettings;
